/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useCallback, useState } from "react";
import { useConditionDisplay } from "./hooks";
import { IEvent } from "../../../store/api/devices/types";
import CustomDataGrid from "../../../components/Shared/Table/CustomDataGrid";
import EventsFilter from "../../../components/Nodes/EventsFilter/EventsFilter";
import { useEventsFilter } from "../../../components/Nodes/EventsFilter/hooks";
import { GridEventListener, GridRowParams, GridSortModel } from "@mui/x-data-grid";
import { getSortBy } from "../../../utils/helpers";
import EventDrawer from "../../../components/Drawers/EventDrawer";
import { useSelector } from "react-redux";
import { useStoreDispatch } from "../../../store/hooks";
import { resetEventFilters } from "../../../store/filters/events/EventFilterReducer";
import { resetKeywordForScope } from "../../../store/search/reducer";
import { searchKeywordByScopeSelector } from "../../../store/search/selector";
import { setRegionFilterIds } from "../../../store/filters/Nodes/reducer";
import { Button } from "../../../components/ui/button";
import { Icon } from "../../../components/Shared/Icon";
import { Loader2 } from "lucide-react";
import ActiveFilterChips from "../../../components/Shared/Filter/ActiveFilterChips";

const ConditionDisplay = () => {
  const dispatch = useStoreDispatch();
  const { data, isLoading, conditionsColumns, exportHandler, exportStatus, setSortOrderToCondition } = useConditionDisplay();
  const { eventFilters: conditionFilters } = useEventsFilter();
  const conditionSearchKeyword = useSelector(searchKeywordByScopeSelector("conditions"));
  const rowsData = data?.data || [];
  const noOfRows = data?.totalItems || 0;

  const handleClearConditionSearch = useCallback(() => {
    dispatch(resetKeywordForScope({ scope: "conditions" }));
  }, [dispatch]);

  const handleClearAllConditionFilters = useCallback(() => {
    dispatch(resetEventFilters());
    dispatch(setRegionFilterIds([]));
    dispatch(resetKeywordForScope({ scope: "conditions" }));
  }, [dispatch]);

  const [rowClicked, setRowClicked] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<IEvent>();

  const handleCloseButtonClicked = (value: boolean) => {
    setRowClicked(value);
  };

  const handleRowClicked: GridEventListener<"rowClick"> = (params: GridRowParams<IEvent>) => {
    setRowClicked(true);
    setSelectedRowData(params.row);
  };

  return (
    <div>
      {rowClicked && <EventDrawer data={selectedRowData} setRowClicked={setRowClicked} closedButtonClicked={handleCloseButtonClicked} isAlarmContext={false} />}
      <div>
        <EventsFilter searchScope="conditions" />
      </div>
      <div className="flex justify-between items-end mb-1 mt-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">{`Showing ${noOfRows} results`}</span>
          <ActiveFilterChips
            filters={conditionFilters}
            searchKeyword={conditionSearchKeyword}
            onClearSearch={handleClearConditionSearch}
            onClearAll={handleClearAllConditionFilters}
            dispatch={dispatch}
          />
        </div>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0 relative -top-1"
          disabled={exportStatus.isLoading}
          onClick={exportHandler}
          aria-label="Export CSV"
        >
          {exportStatus.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon name="download" size={18} />}
        </Button>
      </div>
      <div>
        <CustomDataGrid
          tableId="conditionDisplay"
          rows={rowsData}
          columns={conditionsColumns}
          checkboxSelection={false}
          rowCount={noOfRows}
          loading={isLoading}
          disableColumnMenu={true}
          getRowId={(row: IEvent) => row.objectKey}
          onSortModelChange={(params: GridSortModel) => setSortOrderToCondition(getSortBy(params))}
          onRowClick={handleRowClicked}
          fillContainer
        />
      </div>
    </div>
  );
};

export default ConditionDisplay;
